// src/context/OrgContext.tsx
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import axiosClient from '../api/axiosClient';
import { useAuth } from './AuthContext';

/** Ключ localStorage, в котором живёт выбранная организация. */
export const SELECTED_ORG_STORAGE_KEY = 'orion_selected_org';

/** Организация в том виде, в котором её отдаёт GET /users/me. */
export interface Organization {
    id: string;
    name: string;
    type: string;
}

export interface OrgContextValue {
    /**
     * Выбранная организация.
     * Пустая строка — «все организации / личное», то есть без ограничения по организации.
     */
    orgId: string;
    /** Список организаций текущего пользователя. */
    organizations: Organization[];
    /** Закреплённая (по умолчанию) организация или null, если пользователь ничего не закреплял. */
    pinnedOrgId: string | null;
    /**
     * Выбрать организацию.
     * pin: true дополнительно закрепляет её на бэкенде (POST /users/default-org).
     */
    setOrg: (orgId: string, options?: { pin?: boolean }) => Promise<void>;
}

const OrgContext = createContext<OrgContextValue | undefined>(undefined);

const readStoredOrg = (): string | null => {
    try {
        return localStorage.getItem(SELECTED_ORG_STORAGE_KEY);
    } catch {
        /* localStorage может быть недоступен (приватный режим) */
        return null;
    }
};

const writeStoredOrg = (orgId: string): void => {
    try {
        localStorage.setItem(SELECTED_ORG_STORAGE_KEY, orgId);
    } catch {
        /* localStorage может быть недоступен (приватный режим) */
    }
};

const readCachedUser = (): Record<string, unknown> | null => {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed as Record<string, unknown>;
    } catch {
        /* битый кэш профиля игнорируем */
        return null;
    }
};

/** Закреплённая организация из локального кэша профиля (до ответа GET /users/me). */
const readPinnedFromCachedUser = (): string | null => {
    const cached = readCachedUser();
    const value = cached?.defaultOrganizationId;
    return typeof value === 'string' && value !== '' ? value : null;
};

/**
 * Обновляем локальный кэш профиля, чтобы закреплённая организация не «слетала»
 * до следующего ответа GET /users/me.
 */
const writeDefaultOrgToCachedUser = (orgId: string): void => {
    const cached = readCachedUser();
    if (!cached) return;
    cached.defaultOrganizationId = orgId;
    cached.defaultContext = orgId;
    try {
        localStorage.setItem('user', JSON.stringify(cached));
    } catch {
        /* localStorage может быть недоступен (приватный режим) */
    }
};

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();

    // Начальное значение: localStorage['orion_selected_org'] → user.defaultContext → ''
    const [orgId, setOrgId] = useState<string>(() => readStoredOrg() ?? '');
    const [pinnedOrgId, setPinnedOrgId] = useState<string | null>(() => readPinnedFromCachedUser());

    const organizations = useMemo<Organization[]>(() => {
        const list = user?.organizations;
        if (!Array.isArray(list)) return [];
        return list.map((org) => ({
            id: String(org.id),
            name: org.name,
            // В User.organizations тип организации объявлен не всегда —
            // читаем его осторожно, без any.
            type: (org as { type?: string }).type ?? '',
        }));
    }, [user]);

    // Профиль приходит из AuthContext асинхронно: применяем defaultContext один раз,
    // только если пользователь ещё не выбирал организацию сам.
    const defaultApplied = useRef(false);
    useEffect(() => {
        if (!user || defaultApplied.current) return;
        if (readStoredOrg() !== null) {
            defaultApplied.current = true;
            return;
        }
        defaultApplied.current = true;
        const fallback = user.defaultContext ?? '';
        if (fallback) setOrgId(fallback);
    }, [user]);

    // Бэкенд может прислать закреплённую организацию полем defaultOrganizationId —
    // если пришло, это самый свежий источник правды.
    useEffect(() => {
        const backendDefault = user?.defaultOrganizationId;
        if (typeof backendDefault === 'string' && backendDefault !== '') {
            setPinnedOrgId(backendDefault);
        }
    }, [user]);

    const setOrg = useCallback(async (nextOrgId: string, options?: { pin?: boolean }): Promise<void> => {
        setOrgId(nextOrgId);
        writeStoredOrg(nextOrgId);

        // '' — «все организации / личное»: закреплять нечего, запрос не отправляем
        // (иначе в default_organization_id уедет пустая строка вместо UUID).
        if (!options?.pin || !nextOrgId) return;

        await axiosClient.post('/users/default-org', { orgId: nextOrgId });
        setPinnedOrgId(nextOrgId);
        writeDefaultOrgToCachedUser(nextOrgId);
    }, []);

    const value = useMemo<OrgContextValue>(
        () => ({ orgId, organizations, pinnedOrgId, setOrg }),
        [orgId, organizations, pinnedOrgId, setOrg]
    );

    return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};

export const useOrg = (): OrgContextValue => {
    const context = useContext(OrgContext);
    if (!context) {
        throw new Error('useOrg must be used within an OrgProvider');
    }
    return context;
};
